import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Loader2, ShieldCheck } from 'lucide-react';
import { customerApi } from '@/lib/customerApi';
import { formatApiError } from '@/lib/api';

const money = (v) => Number(v || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

const appearance = {
  theme: 'stripe',
  labels: 'auto',
  inputs: 'spaced',
  variables: {
    borderRadius: '4px',
    colorBackground: '#ffffff',
    colorDanger: '#df1b41',
    colorPrimary: '#0570de',
    colorSuccess: '#00c853',
    colorText: '#30313d',
    fontFamily: 'default',
    fontSizeBase: '16px',
    spacingUnit: '4px',
  },
};

export const StripeQuotePayment = ({ item, config }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);
  const container = useRef(null);
  const q = item.quote;
  const id = `${item.enquiry_id}-v${q.version}`;
  if (q.purchase_type === 'fixed_catalogue' || q.staff_release_required === false) return null;
  const terminal = ['confirmed', 'review_required', 'stripe_expired', 'stripe_failed'].includes(item.payment?.status);
  const otherGateway = item.payment && item.payment.gateway !== 'stripe';
  const disabled = !config?.enabled || terminal || otherGateway;
  const live = config?.mode === 'live';

  const pay = async () => {
    setBusy(true); setError('');
    try {
      if (!window.Stripe || !config?.publishable_key) throw new Error('stripe-unavailable');
      const { data } = await customerApi.post('/payments/checkout', { lookup_key: `${item.enquiry_id}:v${q.version}`, fingerprint: q.fingerprint, origin_url: window.location.origin });
      const stripeClient = window.Stripe(config.publishable_key);
      const checkout = await stripeClient.createEmbeddedCheckoutPage({ clientSecret: data.client_secret });
      setMounted(true);
      checkout.mount(container.current);
      setBusy(false);
    } catch (failure) {
      setError(failure?.message === 'stripe-unavailable' ? (live ? 'Card payment could not be opened. No charge was made.' : 'Stripe test checkout is not available in this preview.') : formatApiError(failure, live ? 'Card payment could not be opened. No charge was made.' : 'Stripe test checkout could not be opened. No additional charge was requested.'));
      setBusy(false);
    }
  };

  return (
    <section className="mt-4 border border-ei-mist bg-ei-mist/20 rounded-md p-4" data-testid={`stripe-quote-${id}`}>
      <div className="flex items-center gap-2 font-semibold text-ei-navy" data-testid={`stripe-mode-${id}`}><CreditCard size={18} />{live ? 'Secure card payment · Stripe' : 'Stripe · test mode only'}</div>
      <p className="text-sm mt-2 text-ei-graphite/80">Charges your accepted quote total, which already includes GST. No extra tax is added{live ? ' and no card surcharge is applied.' : ', no stock is reserved, no goods are supplied and no Shopify/Xero write occurs.'}</p>
      {!live && <p className="text-xs mt-2">Use a Stripe test card only. Never enter a real card in this preview.</p>}
      {!config?.enabled && <p className="mt-3 text-sm" data-testid={`stripe-disabled-${id}`}><ShieldCheck className="inline mr-1" size={15} />{config?.reasons?.join(' ') || 'Card payment is not available right now.'}</p>}
      {otherGateway && <p className="mt-2 text-sm">An existing PayPal attempt must be reconciled before changing payment methods.</p>}
      {error && <p className="mt-3 text-sm text-red-800" role="alert" data-testid={`stripe-error-${id}`}>{error}</p>}
      {!terminal && !mounted && (
        <button type="button" disabled={disabled || busy} onClick={pay} className="mt-4 inline-flex items-center gap-2 min-h-[44px] px-5 py-3 bg-ei-navy text-white rounded-sm font-semibold text-sm disabled:opacity-50" data-testid={`stripe-pay-${id}`}>
          {busy ? <><Loader2 size={16} className="animate-spin" />{live ? 'Opening secure checkout…' : 'Preparing secure test payment…'}</> : item.payment?.gateway === 'stripe' ? (live ? 'Resume your secure card payment' : 'Resume the same Stripe test checkout') : (live ? 'Pay securely by card' : 'Pay with Stripe · test mode')}
        </button>
      )}
      <div ref={container} className={mounted ? 'mt-4' : 'hidden'} data-testid={`stripe-form-${id}`} />
      <div className="mt-4 border-t border-ei-mist pt-3 text-xs leading-relaxed text-ei-graphite/80" data-testid={`stripe-policies-${id}`}>
        <p data-testid={`stripe-gst-note-${id}`}>The amount charged is your accepted total of {money(q.total)} including GST. We do not add a card surcharge, and no other fee is applied at payment.</p>
        <p className="mt-2">By paying you confirm the released quote terms above and our{' '}
          <Link to="/policies/terms" className="font-semibold text-ei-navy underline" data-testid={`stripe-policy-terms-${id}`}>terms of sale</Link>,{' '}
          <Link to="/policies/delivery" className="font-semibold text-ei-navy underline" data-testid={`stripe-policy-delivery-${id}`}>shipping &amp; delivery policy</Link>,{' '}
          <Link to="/policies/returns" className="font-semibold text-ei-navy underline" data-testid={`stripe-policy-returns-${id}`}>returns &amp; claims policy</Link>,{' '}
          <Link to="/policies/warranty" className="font-semibold text-ei-navy underline" data-testid={`stripe-policy-warranty-${id}`}>warranty</Link> and{' '}
          <Link to="/policies/privacy" className="font-semibold text-ei-navy underline" data-testid={`stripe-policy-privacy-${id}`}>privacy policy</Link>.
        </p>
      </div>
    </section>
  );
};
